import { FC } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

import { DesignToken, PromoBannerV2, Text } from "@livechat/design-system-react-components";

import { newFeaturesCampaigns } from "constants/campaigns";
import { LANGUAGE_BASED_ASSIGNMENT } from "constants/rules/premade-rules";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { AgentsRoutes } from "routes/agents/constants";
import { AutomationsRoutes } from "routes/automations/constants";
import { selectIsAdmin } from "store/entities/agents";
import { withVar } from "themes/helpers";

const promoCopy = {
  admin: {
    description:
      "Now, when a message arrives, its language is automatically detected so you can assign the right team or agent. Fine-tune your rule conditions to provide the right language support.",
    cta: "Try rule template",
  },
  nonAdmin: {
    description:
      "Now, when a message arrives, its language is automatically detected so you can assign the right team or agent. Ask admins to use them as conditions in rules to provide the right language support.",
    cta: "Find admins",
  },
};

export const LanguageDetectionPromo: FC = () => {
  const history = useHistory();
  const isAdmin = useSelector(selectIsAdmin);
  const {
    isNewForAgent,
    setAgentFlagCallback: markPromoClosed,
    featureFlagValue: wasPromoClosed,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasClosedLanguageDetectionPromo.flag,
    newFeaturesCampaigns.hasClosedLanguageDetectionPromo.campaignStartDate
  );

  const {
    isNewForAgent: isLanguageConditionNewForAgent,
    setAgentFlagCallback: markLanguageConditionAsSeen,
    featureFlagValue: hasSeenLanguageCondition,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenLanguageCondition.flag,
    newFeaturesCampaigns.hasSeenLanguageCondition.campaignStartDate
  );

  const handlePromoClose = (): void => {
    trackEngagement("Ticket details: Language detection promo closed", EventPlace.TicketDetails);
    markPromoClosed();
  };

  if (!isNewForAgent || wasPromoClosed) {
    return null;
  }

  const { description, cta } = isAdmin ? promoCopy.admin : promoCopy.nonAdmin;

  const handleCtaClick = (): void => {
    trackEngagement("Ticket details: Language detection promo clicked", EventPlace.TicketDetails);
    history.push(isAdmin ? AutomationsRoutes.AddRule : AgentsRoutes.Agents, {
      rule: JSON.stringify(LANGUAGE_BASED_ASSIGNMENT),
    });

    if (isAdmin && !hasSeenLanguageCondition && isLanguageConditionNewForAgent) {
      markLanguageConditionAsSeen();
    }
  };

  return (
    <PromoBannerV2
      onClose={handlePromoClose}
      css={{
        background: withVar(DesignToken.SurfaceOtherBot),
      }}
      primaryButton={{
        kind: "text",
        label: cta,
        handleClick: handleCtaClick,
      }}
    >
      <Text bold size="sm">
        Language detection is live! 🚀
      </Text>
      <Text size="sm">{description}</Text>
    </PromoBannerV2>
  );
};
